import { useFeatureFlags } from "../../contexts/FeatureFlagsContext";

/**
 * Component for conditionally rendering content based on feature flags
 *
 * @param {Object} props
 * @param {string} props.flag - The feature flag name to check
 * @param {React.ReactNode} props.children - Content to render if flag is enabled
 * @param {React.ReactNode} [props.fallback] - Optional content to render if flag is disabled
 * @param {React.ReactNode} [props.loadingFallback] - Optional content to render while flags are loading
 *
 * @example
 * <FeatureFlag flag="alerts_v2" fallback={<p>Coming soon!</p>}>
 *   <AlertsDashboard />
 * </FeatureFlag>
 */
export const FeatureFlag = ({
  flag,
  children,
  fallback = null,
  loadingFallback = null,
}) => {
  const { isEnabled, isLoading } = useFeatureFlags();

  // Don't render anything (or loading fallback) while flags are still loading
  // This prevents flash of incorrect content during the loading race condition
  if (isLoading) {
    return loadingFallback;
  }

  if (!isEnabled(flag)) {
    return fallback;
  }

  return children;
};

/**
 * Component to show content only when a feature is disabled
 *
 * @param {Object} props
 * @param {string} props.flag - The feature flag name to check
 * @param {React.ReactNode} props.children - Content to render if flag is disabled
 * @param {React.ReactNode} [props.loadingFallback] - Optional content to render while flags are loading
 */
export const FeatureDisabled = ({ flag, children, loadingFallback = null }) => {
  const { isEnabled, isLoading } = useFeatureFlags();

  // Don't render anything while flags are still loading
  // This prevents showing "disabled" content before we know the true flag state
  if (isLoading) {
    return loadingFallback;
  }

  if (isEnabled(flag)) {
    return null;
  }

  return children;
};

export default FeatureFlag;
